import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  stages: string[];

  /** counting start from 0, default 0 */
  current?: number;
}

interface IStage {
  isLastStage: boolean;

  description: string;

  step: number;
}

export const StepProgress: React.FC<IProps> = ({ stages, current = 0 }) => {
  const { t } = useTranslation();
  const renderSteps = stages.map((stage, step) => {
    // in last step, we remove w-full class in order to CSS to work
    const currentStep = step + 1;
    const isLastStage = useMemo(() => currentStep === stages.length, [step, current]);
    const description = t(stage);

    if (step < current)
      return (
        <CompletedStage
          key={stage}
          step={currentStep}
          isLastStage={isLastStage}
          description={description}
        />
      );

    if (step > current)
      return (
        <PendingStage
          key={stage}
          step={currentStep}
          isLastStage={isLastStage}
          description={description}
        />
      );

    return (
      <InProgressStage
        key={stage}
        step={currentStep}
        isLastStage={isLastStage}
        description={description}
      />
    );
  });

  return (
    <div className="w-5/6 mx-auto">
      <div className="bg-gray-200 h-1 flex items-center justify-between">
        {renderSteps}
      </div>
    </div>
  );
};

const InProgressStage: React.FC<IStage> = ({ isLastStage, description, step }) => {
  return (
    <div className={`flex h-1 items-center ${isLastStage ? '' : 'w-full'}`}>
      <div className="flex items-center justify-center bg-white h-6 w-6 rounded-full shadow -ml-2 relative">
        <div className="bg-wine-primary h-3 w-3 rounded-full shadow flex-none"></div>
        <Description step={step}>{description}</Description>
      </div>
    </div>
  );
};

const PendingStage: React.FC<IStage> = ({ isLastStage, description, step }) => {
  return (
    <div className={`flex h-1 items-center ${isLastStage ? '' : 'w-full'}`}>
      <div className="flex items-center justify-center bg-white h-6 w-6 rounded-full shadow -ml-2 relative">
        <Description step={step}>{description}</Description>
      </div>
    </div>
  );
};

const CompletedStage: React.FC<IStage> = ({ isLastStage, description, step }) => {
  return (
    <div
      className={`flex h-1 items-center bg-wine-primary ${isLastStage ? '' : 'w-full'}`}
    >
      <div className="flex items-center justify-center bg-wine-primary h-6 w-6 rounded-full shadow -ml-2 relative">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="icon icon-tabler icon-tabler-check"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="#FFFFFF"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path stroke="none" d="M0 0h24v24H0z" />
          <path d="M5 12l5 5l10 -10" />
        </svg>
        <Description step={step}>{description}</Description>
      </div>
    </div>
  );
};

const Description = ({
  children,
  step,
}: {
  children: JSX.Element | string;
  step: number;
}) => {
  const { t } = useTranslation();

  return (
    <div className="absolute top-8 py-1 px-8 bg-white border border-gray-100 shadow-lg rounded-lg break-keep min-w-max shrink">
      <span className="text-wine-primary uppercase text-sm font-bold">
        {`${t('common.step')} ${step}: `}
      </span>
      <span className="text-sm">{children}</span>
    </div>
  );
};
